import { isValidElement, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Clock, X } from "lucide-react";
import {
  getPostBySlug,
  formatDate,
  categorySlug,
  getRelatedPosts,
} from "./posts";
import { useMetaTags } from "../components/MetaTags";
import { TRIAL_DAYS, TRIAL_PLAN_NAME } from "@omnicanvas/core";
import { track } from "../analytics";

// Note: post JSON-LD (BlogPosting + BreadcrumbList + FAQPage + ItemList for
// review listicles) is injected into <head> at prerender time by
// scripts/prerender.ts — the single source of truth, so it's in the crawlable
// HTML without being duplicated here.

// Inline formatting: links [text](url), bold **text**, italic *text*
// Google truncates result titles around 60 characters. The old suffix
// (" - OmniCanvas Blog", 18 chars) pushed the part that earns the click past
// that cut on every long title: "Best Infinite Canvas Apps for Android 2026: I
// Tried 7 - OmniCanvas Blog" is 70 characters, so searchers saw the generic
// front half and lost "I Tried 7" — the differentiator — entirely.
//
// Brand the title only when it still fits. A shorter separator buys back six
// characters, and titles that would overflow ship bare rather than truncated.
const TITLE_MAX = 60;
const BRAND_SUFFIX = " | OmniCanvas";

export function blogPageTitle(title: string): string {
  const branded = `${title}${BRAND_SUFFIX}`;
  return branded.length <= TITLE_MAX ? branded : title;
}

function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let partIdx = 0;
  const linkClass =
    "text-indigo-600 hover:text-indigo-700 font-medium underline underline-offset-2";

  while (remaining.length > 0) {
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const italicMatch = remaining.match(/\*([^*]+?)\*/);
    // Pick whichever match occurs earliest (ties favor links, then bold)
    const candidates = [linkMatch, boldMatch, italicMatch].filter(
      (m): m is RegExpMatchArray => !!m && m.index !== undefined
    );
    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }
    const match = candidates.reduce((a, b) =>
      (a.index ?? 0) <= (b.index ?? 0) ? a : b
    );
    if ((match.index ?? 0) > 0) {
      parts.push(remaining.slice(0, match.index));
    }
    if (match === linkMatch) {
      const href = match[2];
      if (href.startsWith("/")) {
        parts.push(
          <Link key={partIdx++} to={href} className={linkClass}>
            {match[1]}
          </Link>
        );
      } else {
        parts.push(
          <a key={partIdx++} href={href} className={linkClass}>
            {match[1]}
          </a>
        );
      }
    } else if (match[0].startsWith("**")) {
      parts.push(<strong key={partIdx++}>{match[1]}</strong>);
    } else {
      parts.push(<em key={partIdx++}>{match[1]}</em>);
    }
    remaining = remaining.slice((match.index ?? 0) + match[0].length);
  }
  return parts;
}

/**
 * A line that is only an image, `![alt](/images/x.webp "Optional caption")`,
 * renders as a figure. Inline images are not supported; posts use them
 * between paragraphs.
 */
export function parseBlockImage(line: string): { src: string; alt: string; caption: string | null } | null {
  const match = line.trim().match(/^!\[([^\]]*)\]\((\S+?)(?:\s+"([^"]*)")?\)$/);
  if (!match) return null;
  const [, alt, src, caption] = match;
  // Only images this site serves; a post must not pull from elsewhere.
  if (!src.startsWith("/images/")) return null;
  return { src, alt, caption: caption || null };
}

function renderMarkdown(content: string) {
  const lines = content.trim().split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;
  let listItems: string[] = [];

  function flushList() {
    if (listItems.length > 0) {
      elements.push(
        <ul
          key={`ul-${elements.length}`}
          className="list-disc pl-6 space-y-2 text-slate-700 leading-relaxed"
        >
          {listItems.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  }

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      flushList();
      i++;
      continue;
    }

    const image = parseBlockImage(line);
    if (image) {
      flushList();
      elements.push(
        <figure key={`img-${i}`} className="my-8">
          <img
            src={image.src}
            alt={image.alt}
            loading="lazy"
            decoding="async"
            className="h-auto w-full rounded-xl border border-slate-200 shadow-sm"
          />
          {image.caption && (
            <figcaption className="mt-2 text-center text-sm text-slate-500">{image.caption}</figcaption>
          )}
        </figure>
      );
      i++;
      continue;
    }

    if (line.startsWith("## ")) {
      flushList();
      elements.push(
        <h2
          key={`h2-${i}`}
          className="text-2xl font-bold text-slate-900 mt-10 mb-4"
        >
          {line.slice(3)}
        </h2>
      );
      i++;
      continue;
    }

    if (line.startsWith("### ")) {
      flushList();
      elements.push(
        <h3
          key={`h3-${i}`}
          className="text-xl font-semibold text-slate-900 mt-8 mb-3"
        >
          {line.slice(4)}
        </h3>
      );
      i++;
      continue;
    }

    if (line.startsWith("| ")) {
      flushList();
      const tableLines: string[] = [];
      // Collect all table rows, including |---|---| separator rows
      while (i < lines.length && lines[i].startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const isSeparatorRow = (row: string) => /^[\s|:-]+$/.test(row);
      if (tableLines.length >= 2) {
        const headers = tableLines[0]
          .split("|")
          .filter((c) => c.trim())
          .map((c) => c.trim());
        const rows = tableLines
          .slice(1)
          .filter((row) => !isSeparatorRow(row))
          .map((row) =>
            row
              .split("|")
              .filter((c) => c.trim())
              .map((c) => c.trim())
          );
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto my-6">
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-slate-50">
                  {headers.map((h, idx) => (
                    <th
                      key={idx}
                      className="px-4 py-3 text-left font-semibold text-slate-700 border-b border-slate-200"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr
                    key={rIdx}
                    className={rIdx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                  >
                    {row.map((cell, cIdx) => (
                      <td
                        key={cIdx}
                        className="px-4 py-3 text-slate-600 border-b border-slate-100"
                      >
                        {renderInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    const listMatch = line.match(/^- (.+)/);
    if (listMatch) {
      listItems.push(listMatch[1]);
      i++;
      continue;
    }

    const numberedMatch = line.match(/^\d+\.\s+(.+)/);
    if (numberedMatch) {
      flushList();
      // Collect all numbered items
      const olItems: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        const m = lines[i].match(/^\d+\.\s+(.+)/);
        if (m) olItems.push(m[1]);
        i++;
      }
      elements.push(
        <ol
          key={`ol-${elements.length}`}
          className="list-decimal pl-6 space-y-2 text-slate-700 leading-relaxed"
        >
          {olItems.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Regular paragraph
    flushList();
    let text = line;
    // Collect multi-line paragraphs
    while (
      i + 1 < lines.length &&
      lines[i + 1].trim() !== "" &&
      !lines[i + 1].startsWith("#") &&
      !lines[i + 1].startsWith("- ") &&
      !lines[i + 1].startsWith("| ") &&
      !/^\d+\.\s+/.test(lines[i + 1])
    ) {
      i++;
      text += " " + lines[i];
    }

    elements.push(
      <p
        key={`p-${i}`}
        className="text-slate-700 leading-relaxed"
      >
        {renderInline(text)}
      </p>
    );
    i++;
  }

  flushList();
  return elements;
}

type BlogCtaConfig = {
  eyebrow: string;
  headline: string;
  body: string;
  label: string;
  templateId?: string;
};

// Aug–Sep 2026, Google visitors only: the top CTA takes nearly every click, and
// the ones that convert (best-free-infinite-canvas-apps 30%, milanote 22%,
// android 18%, best-whiteboard 17%) all promise one concrete, free thing the
// reader can open right now ("Open a free canvas", "Open a blank whiteboard").
// The ones that didn't (3–6%) were abstract ("Try the ranked pick", "Try the
// canvas workspace"), left out "free", or offered the wrong job (a meeting-notes
// canvas to iPad handwriting searchers). Configs follow the winning shape:
// eyebrow says free + no account, headline names the thing, label is "Open …".
//
// Bump when the copy changes so before/after stays comparable: it rides on the
// app link (cta_copy, read by the cta_click listener) and on blog_cta_view.
export const BLOG_CTA_COPY_VERSION = "2026-09-open-free";

const DEFAULT_BLOG_CTA: BlogCtaConfig = {
  eyebrow: "Free, no account",
  headline: "Put this on an infinite canvas — open one free, right now.",
  body: "Notes, sketches, and ideas anywhere on the canvas, in your browser. Sign up only when you want it synced.",
  label: "Open a free canvas",
};

const BLOG_CTAS_BY_SLUG: Record<string, BlogCtaConfig> = {
  "best-infinite-canvas-app-for-android": {
    eyebrow: "Android-friendly web app",
    headline: "Open a free infinite canvas on your Android tablet.",
    body: "Use OmniCanvas in the browser with spatial notes, sketching, search, and sync back to desktop.",
    label: "Try OmniCanvas on Android",
    templateId: "brainstorm-web",
  },
  "best-infinite-canvas-app-for-note-taking": {
    eyebrow: "For serious note-taking",
    headline: "Start a searchable canvas note, not another throwaway board.",
    body: "OmniCanvas combines infinite space with folders, tags, full-text search, and synced notes.",
    label: "Create a spatial note",
    templateId: "brainstorm-web",
  },
  "scrintal-alternatives": {
    eyebrow: "Free Scrintal alternative",
    headline: "Try the spatial notes workflow before paying for another PKM tool.",
    body: "Build connected cards on a free infinite canvas with web access, Mac sync, and no setup.",
    label: "Try the free alternative",
    templateId: "brainstorm-web",
  },
  "free-notetaking-apps-2026": {
    eyebrow: "Free, no account",
    headline: "Want the visual pick from this list? Open it free, right now.",
    body: "Notes, sketches, and idea maps on an infinite canvas in your browser. Free forever, no credit card, no account to start.",
    label: "Open a free canvas",
    templateId: "brainstorm-web",
  },
  "best-note-taking-apps-for-ipad-2026": {
    eyebrow: "Free on iPad, no account",
    headline: "Open a free infinite canvas on your iPad — write with Apple Pencil, type, and sketch.",
    body: "Runs in Safari or as an installed app, and syncs to your Mac and the web when you sign up.",
    label: "Open a free canvas on iPad",
    templateId: "brainstorm-web",
  },
  "best-infinite-canvas-app-for-ipad": {
    eyebrow: "iPad web canvas",
    headline: "Try a free spatial canvas on your iPad before choosing an app.",
    body: "OmniCanvas works in the browser/PWA, with visual notes that sync to Mac and web.",
    label: "Open the iPad canvas",
    templateId: "brainstorm-web",
  },
  "best-whiteboard-apps": {
    eyebrow: "Free online whiteboard",
    headline: "Open a clean whiteboard now, then save it as a real note.",
    body: "Sketch immediately in your browser. There is no signup wall; create an account only when the board is worth keeping.",
    label: "Open a blank whiteboard",
  },
  "tldraw-alternatives": {
    eyebrow: "Free tldraw alternative, no account",
    headline: "Open a free infinite canvas now — draw, then keep your notes searchable.",
    body: "The freeform canvas feel, plus notes, folders, and full-text search that persist.",
    label: "Open a free canvas",
    templateId: "brainstorm-web",
  },
  "heptabase-alternatives": {
    eyebrow: "Free Heptabase alternative",
    headline: "Build a visual second brain without starting with a subscription.",
    body: "Use linked spatial notes on an infinite canvas with web access and Mac sync.",
    label: "Start your visual second brain",
    templateId: "brainstorm-web",
  },
  "best-infinite-canvas-apps-2026": {
    eyebrow: "Free, no account",
    headline: "Skip the comparison — open a free infinite canvas right now.",
    body: "Spatial notes, drawing, search, and templates in your browser. Sign up only when you want it synced.",
    label: "Open a free canvas",
    templateId: "brainstorm-web",
  },
  "omnicanvas-vs-granola": {
    eyebrow: "Free, no install, no bot",
    headline: "Open a meeting canvas and watch your own notes get filled in.",
    body: "Record in the browser, jot a few words, and get them back expanded from the transcript — with the audio kept.",
    label: "Open the Meeting Notes template",
    templateId: "meeting-notes",
  },
  "omnicanvas-vs-obsidian-canvas": {
    eyebrow: "Test the difference yourself",
    headline: "Build a spatial note without setting up a vault or an account.",
    body: "Open a ready-made mind map, move the branches, and decide from the actual workflow—not a feature table.",
    label: "Try the spatial workflow",
    templateId: "brainstorm-web",
  },
  "miro-alternatives": {
    eyebrow: "Free Miro alternative, no account",
    headline: "Open a free personal canvas now — no workshop setup, no seat to buy.",
    body: "An infinite canvas with notes, search, and collaboration for solo users and small teams.",
    label: "Open a free canvas",
    templateId: "brainstorm-web",
  },
  "milanote-alternatives": {
    eyebrow: "Free mood board canvas",
    headline: "Build your next mood board on an unlimited spatial canvas.",
    body: "Collect references, notes, links, and sketches in one free OmniCanvas workspace.",
    label: "Create a mood board",
    templateId: "brainstorm-web",
  },
  "best-free-infinite-canvas-apps": {
    eyebrow: "Free, no account",
    headline: "Skip the comparison and open a free canvas right now.",
    body: "Unlimited canvas, notes, sketching, and search in the browser. Sign up only when you want it synced.",
    label: "Open a free canvas",
    templateId: "brainstorm-web",
  },
  "apple-freeform-alternatives": {
    eyebrow: "Free Freeform alternative, any device",
    headline: "Open a free canvas on Windows, Android, or the web — no Apple ID, no account.",
    body: "Searchable notes, folders, and tags that Freeform doesn't have, in any browser.",
    label: "Open a free canvas",
    templateId: "brainstorm-web",
  },
  "concept-lines-see-how-ideas-connect": {
    eyebrow: "Concept lines, on every plan",
    headline: "Write a few blocks, then click Concepts.",
    body: "Open a canvas in the browser with no account. Concepts sits at the top right of the canvas on windows 1,024 pixels and wider; matching by meaning is an optional on-device download.",
    label: "Open a canvas",
  },
  "photograph-your-notebook-ocr": {
    eyebrow: "Turn photos into searchable notes",
    headline: "Photograph a page, get text you can search.",
    body: "Scan or upload a photo of a handwritten page. The image stays exactly as you wrote it; the extracted text rides alongside it in full-text search.",
    label: "Scan a notebook page",
  },
  "what-ipad-note-taking-reviews-agree-on": {
    eyebrow: "iPad notes, tested not ranked",
    headline: "Skip another ranked list — test the search and process yourself.",
    body: "Open a free canvas on your iPad, capture a few notes, and see how search and organization actually hold up before you commit to an app.",
    label: "Try it on your iPad",
    templateId: "brainstorm-web",
  },
  "tablet-vs-laptop-notetaking": {
    eyebrow: "Works on tablet and laptop",
    headline: "Whichever device you picked, put it to the test with a real canvas.",
    body: "OmniCanvas runs the same spatial notes in the browser on tablet and desktop, so your notes carry over when you switch devices.",
    label: "Try it on your device",
    templateId: "brainstorm-web",
  },
  "goodnotes-alternatives": {
    eyebrow: "Free on iPad, no account",
    headline: "Handwrite with Apple Pencil on an infinite canvas — open one free, right now.",
    body: "No page edges: write, sketch, and type anywhere, in Safari or as an installed app.",
    label: "Open a free canvas on iPad",
    templateId: "brainstorm-web",
  },
  "obsidian-canvas-alternatives": {
    eyebrow: "Free, no account",
    headline: "Open a free canvas with real drawing tools and smooth zoom — no vault to set up.",
    body: "Freehand drawing, smooth pan/zoom, folders, tags, and full-text search in your browser.",
    label: "Open a free canvas",
    templateId: "brainstorm-web",
  },
};

function ctaForPost(post: NonNullable<ReturnType<typeof getPostBySlug>>): BlogCtaConfig {
  const configured = BLOG_CTAS_BY_SLUG[post.slug];
  if (configured) return configured;
  if (post.cta) {
    return {
      ...DEFAULT_BLOG_CTA,
      label: `Open the ${post.cta.templateName} template`,
      templateId: post.cta.templateId,
    };
  }
  return DEFAULT_BLOG_CTA;
}

type CtaPosition = "top" | "mid" | "bottom" | "sticky";

function appCtaHref(slug: string, position: CtaPosition, templateId?: string): string {
  const params = new URLSearchParams({
    mode: "try",
    utm_source: "blog",
    utm_medium: "organic",
    utm_campaign: slug,
    utm_content: `${position}_blog_cta`,
    cta_copy: BLOG_CTA_COPY_VERSION,
  });
  if (templateId) params.set("template", templateId);
  else params.set("action", "new-note");
  return `https://app.omnicanvasnotes.com/?${params.toString()}`;
}

function demoCtaHref(slug: string, position: "top" | "bottom"): string {
  const params = new URLSearchParams({
    utm_source: "blog",
    utm_medium: "demo",
    utm_campaign: slug,
    utm_content: `${position}_demo_cta`,
  });
  return `/demo?${params.toString()}`;
}

function useBlogEngagementTracking(slug: string | undefined, category: string | undefined) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const midCtaRef = useRef<HTMLDivElement>(null);
  const bottomCtaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) return;
    const seenDepths = new Set<number>();
    const depths = [25, 50, 75, 90];

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = Math.round((window.scrollY / scrollable) * 100);
      for (const depth of depths) {
        if (pct >= depth && !seenDepths.has(depth)) {
          seenDepths.add(depth);
          track("blog_scroll_depth", { slug, category, depth });
        }
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug, category]);

  // Only the top CTA reported impressions, so `blog_cta_view` was really a
  // top-of-post metric being read as a whole-page one. Each position now gets
  // its own observer, which pairs with the `position` prop on `cta_click`.
  useEffect(() => {
    if (!slug || typeof IntersectionObserver === "undefined") return;
    const variant = BLOG_CTAS_BY_SLUG[slug]?.eyebrow ? "page_specific" : "default";
    const observers: IntersectionObserver[] = [];
    for (const [position, ref] of [
      ["top", ctaRef],
      ["mid", midCtaRef],
      ["bottom", bottomCtaRef],
    ] as const) {
      const node = ref.current;
      if (!node) continue;
      let tracked = false;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!tracked && entry.isIntersecting) {
            tracked = true;
            track("blog_cta_view", { slug, category, position, variant, copy: BLOG_CTA_COPY_VERSION });
            observer.disconnect();
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(node);
      observers.push(observer);
    }
    return () => observers.forEach((observer) => observer.disconnect());
  }, [slug, category]);

  return { ctaRef, midCtaRef, bottomCtaRef };
}

function BlogTopCta({
  post,
  ctaRef,
}: {
  post: NonNullable<ReturnType<typeof getPostBySlug>>;
  ctaRef: RefObject<HTMLDivElement>;
}) {
  const cta = ctaForPost(post);
  const href = appCtaHref(post.slug, "top", cta.templateId);

  return (
    <div
      ref={ctaRef}
      className="mb-10 rounded-xl border border-indigo-100 bg-indigo-50 p-5 sm:p-6"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase text-indigo-700">
            {cta.eyebrow}
          </p>
          <p className="mt-1 text-base font-semibold text-slate-900">
            {cta.headline}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {cta.body}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-stretch gap-1.5 sm:items-end">
          <a
            href={href}
            className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
          >
            {cta.label}
          </a>
          {/* A demo link sat here and sent readers one step further from the
              app: of 141 demo sessions since August, 5 ever reached the app. */}
          <p className="text-center text-xs text-slate-500 sm:text-right">
            Free forever · {TRIAL_DAYS} days of {TRIAL_PLAN_NAME} when you sign up
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * A comparison post puts the reader's decision moment in the middle of the
 * article — long after the top CTA scrolled away and long before the footer
 * one. This bar covers that gap: it appears once the reader is genuinely
 * reading (30% depth), retires as soon as the footer CTA is on screen so the
 * two never compete, and stays dismissed for the rest of the session.
 */
/**
 * The footer CTA is reached by ~3% of readers (90%-scroll fires for 35 people
 * against 717 at 25%), so the only in-flow conversion point that most of the
 * article's audience ever sees is the one above the fold. This one sits at the
 * first section break past ~40% of the body, which is inside the band where
 * scroll-depth still reports real readership.
 */
function BlogMidCta({
  post,
  midCtaRef,
}: {
  post: NonNullable<ReturnType<typeof getPostBySlug>>;
  midCtaRef: RefObject<HTMLDivElement>;
}) {
  const cta = ctaForPost(post);

  return (
    <div
      ref={midCtaRef}
      className="my-10 flex flex-col gap-4 rounded-xl border border-indigo-100 bg-indigo-50/70 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div className="min-w-0">
        <p className="font-semibold text-slate-900">{cta.eyebrow}</p>
        <p className="mt-1 text-sm text-slate-600">
          Open a canvas in the browser — no account needed.
        </p>
      </div>
      <a
        href={appCtaHref(post.slug, "mid", cta.templateId)}
        className="shrink-0 self-start rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 sm:self-auto"
      >
        {cta.label}
      </a>
    </div>
  );
}

/**
 * Where to break the rendered body for the mid-article CTA: the first heading
 * at or past 40% of the elements, so the CTA lands on a section boundary
 * rather than between two paragraphs of the same argument. Falls back to the
 * 40% mark itself, and to "don't split at all" for posts too short to earn a
 * second CTA.
 */
export function midCtaSplitIndex(elements: React.ReactNode[]): number {
  if (elements.length < 8) return -1;
  const target = Math.floor(elements.length * 0.4);
  for (let i = target; i < elements.length - 2; i++) {
    const el = elements[i];
    if (isValidElement(el) && (el.type === "h2" || el.type === "h3")) return i;
  }
  return target;
}

function BlogStickyCta({
  post,
  bottomCtaRef,
}: {
  post: NonNullable<ReturnType<typeof getPostBySlug>>;
  bottomCtaRef: RefObject<HTMLDivElement>;
}) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const cta = ctaForPost(post);
  const viewTracked = useRef(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(`oc_blog_sticky_dismissed:${post.slug}`)) setDismissed(true);
    } catch {
      /* private mode can block sessionStorage without breaking the CTA */
    }
  }, [post.slug]);

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = (window.scrollY / scrollable) * 100;
      const footer = bottomCtaRef.current;
      const footerOnScreen = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
      setVisible(pct >= 30 && !footerOnScreen);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [dismissed, bottomCtaRef]);

  useEffect(() => {
    if (!visible || viewTracked.current) return;
    viewTracked.current = true;
    track("blog_cta_view", {
      slug: post.slug,
      category: post.category,
      position: "sticky",
      variant: BLOG_CTAS_BY_SLUG[post.slug]?.eyebrow ? "page_specific" : "default",
      copy: BLOG_CTA_COPY_VERSION,
    });
  }, [visible, post.slug, post.category]);

  if (dismissed || !visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-indigo-100 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_16px_rgba(15,23,42,0.08)]">
      <div className="mx-auto flex max-w-3xl items-center gap-3">
        <p className="min-w-0 flex-1 truncate text-sm text-slate-700">
          <span className="font-semibold text-slate-900">{cta.eyebrow}.</span>{" "}
          <span className="hidden sm:inline">No account needed.</span>
        </p>
        <a
          href={appCtaHref(post.slug, "sticky", cta.templateId)}
          className="shrink-0 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
        >
          {cta.label}
        </a>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            setDismissed(true);
            try {
              sessionStorage.setItem(`oc_blog_sticky_dismissed:${post.slug}`, "1");
            } catch {
              /* dismissal simply won't persist */
            }
          }}
          className="shrink-0 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const relatedPosts = slug ? getRelatedPosts(slug, 3) : [];
  const { ctaRef, midCtaRef, bottomCtaRef } = useBlogEngagementTracking(post?.slug, post?.category);
  const renderedContent = useMemo(
    () => (post ? renderMarkdown(post.content) : null),
    [post]
  );
  const splitIndex = useMemo(
    () => (renderedContent ? midCtaSplitIndex(renderedContent) : -1),
    [renderedContent]
  );

  const canonicalUrl = post
    ? `https://omnicanvasnotes.com/blog/${categorySlug(post.category)}/${post.slug}`
    : "https://omnicanvasnotes.com/blog";
  const postImage = post && post.coverImage !== null
    ? (post.coverImage ?? `https://pub-530c8de906034aa3846ea946c751a5aa.r2.dev/${post.slug}.png`)
    : undefined;

  useMetaTags({
    title: post ? blogPageTitle(post.title) : "Post Not Found | OmniCanvas",
    description: post?.description ?? "",
    ogImage: postImage,
    url: canonicalUrl,
    type: post ? "article" : "website",
    articlePublishedTime: post?.date,
  });

  if (!post) {
    return (
      <div className="min-h-screen bg-white pt-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            Post not found
          </h1>
          <Link
            to="/blog"
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Back to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <article className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8">
            <Link to="/blog" className="hover:text-slate-700 transition-colors">
              Blog
            </Link>
            <ChevronRight size={14} />
            <Link
              to={`/blog/${categorySlug(post.category)}`}
              className="hover:text-slate-700 transition-colors"
            >
              {post.category}
            </Link>
            <ChevronRight size={14} />
            <span className="text-slate-900 font-medium truncate max-w-[200px]">
              {post.title}
            </span>
          </nav>

          <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
            <span>
              {post.updated && post.updated > post.date
                ? `Updated ${formatDate(post.updated)}`
                : formatDate(post.date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={13} />
              {post.readTime}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-8 leading-tight">
            {post.title}
          </h1>

          {post.coverImage !== null && (
            <img
              src={post.coverImage ?? `https://pub-530c8de906034aa3846ea946c751a5aa.r2.dev/${post.slug}.png`}
              alt={post.title}
              className="w-full rounded-xl mb-10 shadow-sm"
              loading="eager"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}

          <BlogTopCta post={post} ctaRef={ctaRef} />
          <BlogStickyCta post={post} bottomCtaRef={bottomCtaRef} />

          <div className="prose-content space-y-4">
            {splitIndex < 0 ? (
              renderedContent
            ) : (
              <>
                {renderedContent?.slice(0, splitIndex)}
                <BlogMidCta post={post} midCtaRef={midCtaRef} />
                {renderedContent?.slice(splitIndex)}
              </>
            )}
          </div>

          <div className="mt-16 pt-8 border-t border-slate-100">
            <div ref={bottomCtaRef} className="bg-indigo-50 rounded-xl p-8 text-center">
              {post.cta ? (
                <>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Try the {post.cta.templateName} template in OmniCanvas
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Open a local canvas with the{" "}
                    {post.cta.templateName} layout ready to go — no setup
                    or account needed.
                  </p>
                  <a
                    href={appCtaHref(post.slug, "bottom", post.cta.templateId)}
                    className="inline-flex bg-indigo-600 text-white font-semibold px-6 py-3 rounded-full hover:bg-indigo-700 transition-colors"
                  >
                    Use the {post.cta.templateName} Template
                  </a>
                  <p className="mt-4 text-sm text-slate-500">
                    Start locally, then sign up only to sync — or{" "}
                    <a href={demoCtaHref(post.slug, "bottom")} className="font-medium text-indigo-700 hover:text-indigo-900">
                      explore the interactive demo
                    </a>{" "}
                    first.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Ready to try spatial notetaking?
                  </h3>
                  <p className="text-slate-600 mb-6">
                    OmniCanvas is a free infinite canvas app for notes, sketches,
                    and ideas. Start locally with no account.
                  </p>
                  <a
                    href={appCtaHref(post.slug, "bottom")}
                    className="inline-flex bg-indigo-600 text-white font-semibold px-6 py-3 rounded-full hover:bg-indigo-700 transition-colors"
                  >
                    Try OmniCanvas Free
                  </a>
                  <p className="mt-4 text-sm text-slate-500">
                    Sign up only when you want sync — or{" "}
                    <a href={demoCtaHref(post.slug, "bottom")} className="font-medium text-indigo-700 hover:text-indigo-900">
                      explore the interactive demo
                    </a>{" "}
                    first.
                  </p>
                </>
              )}
            </div>
          </div>

          {relatedPosts.length > 0 && (
            <div className="mt-16 pt-10 border-t border-slate-100">
              <h2 className="text-xl font-bold text-slate-900 mb-6">
                Keep reading
              </h2>
              <div className="grid gap-5 sm:grid-cols-3">
                {relatedPosts.map((rp) => (
                  <Link
                    key={rp.slug}
                    to={`/blog/${categorySlug(rp.category)}/${rp.slug}`}
                    className="group block rounded-xl border border-slate-200 p-5 hover:border-indigo-300 hover:shadow-md transition-all"
                  >
                    <span className="text-xs font-medium text-indigo-600">
                      {rp.category}
                    </span>
                    <h3 className="mt-2 font-semibold text-slate-900 leading-snug group-hover:text-indigo-700 transition-colors">
                      {rp.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500 line-clamp-2">
                      {rp.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}
